import { Button, Modal } from "components"
import type { MovementControlRow } from "../types/inventoryMovement"
import {
  auditExplanation,
  formatNumberId,
  smartNumber
} from "../utils/inventoryMovementHelpers"

const PRODUCT_PLACEHOLDER_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="22" fill="%23374151">PRODUCT</text></svg>`
  )

type AuditDetailModalProps = {
  row: MovementControlRow | null
  onClose: () => void
}

export function AuditDetailModal({ row, onClose }: AuditDetailModalProps) {
  const isOpen = row !== null

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title="TRANSACTION AUDIT DETAIL"
      size="xl"
      isdarktitle
      zIndexClassName="relative z-[110]">
      <Modal.Body className="!h-auto max-h-[70vh] overflow-y-auto">
        {row ? (
          <div className="text-sm">
            <div className="grid grid-cols-12 gap-4 items-start">
              <div className="col-span-12 md:col-span-2">
                <img
                  src={PRODUCT_PLACEHOLDER_SVG}
                  className="h-28 w-full border border-base-300 bg-base-100 object-cover"
                  alt=""
                />
              </div>
              <div className="col-span-12 md:col-span-10 space-y-3">
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="text-lg font-extrabold leading-tight">
                      Velvet Cushion Cover Luxe 50x30 Blush
                    </div>
                    <div className="mt-1 text-xs text-base-content/60">
                      Code: ITM-0001 | Category: Cushion Cover | UOM: PCS
                    </div>
                  </div>
                  <div className="min-w-[150px] overflow-hidden border border-base-300 px-3 py-2 text-center">
                    <div className="text-[10px] font-extrabold uppercase tracking-wide text-base-content/50">
                      Average Cost
                    </div>
                    <div
                      className="mt-1 text-lg font-black leading-tight tabular-nums"
                      title={formatNumberId(row.cost)}>
                      {smartNumber(row.cost)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <AuditBox label="Date" value={row.date} />
                  <AuditBox label="Transaction" value={row.transactionType} />
                  <AuditBox label="Reference" value={row.reference} />
                  <AuditBox label="Location" value={row.location} />
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
              <AuditBox
                label="Opening"
                value={smartNumber(row.opening)}
                title={formatNumberId(row.opening)}
              />
              <AuditBox
                label="IN"
                value={smartNumber(row.stockIn)}
                title={formatNumberId(row.stockIn)}
              />
              <AuditBox
                label="OUT"
                value={smartNumber(row.stockOut)}
                title={formatNumberId(row.stockOut)}
              />
              <AuditBox
                label="Expected"
                value={smartNumber(row.expectedClosing)}
                title={formatNumberId(row.expectedClosing)}
              />
              <AuditBox
                label="Actual"
                value={smartNumber(row.actualClosing)}
                title={formatNumberId(row.actualClosing)}
              />
              <AuditBox
                label="Variance"
                value={smartNumber(row.variance)}
                title={formatNumberId(row.variance)}
              />
              <AuditBox label="Status" value={row.status} />
              <div className="col-span-2 flex min-h-[70px] flex-col justify-center border border-base-300 bg-base-200/40 px-2 py-2 text-center md:col-span-2 xl:col-span-2">
                <div className="text-[10px] font-extrabold uppercase tracking-wide text-base-content/50">
                  Value
                </div>
                <div
                  className="text-xl font-black leading-tight tabular-nums md:text-2xl"
                  title={formatNumberId(row.value)}>
                  {smartNumber(row.value)}
                </div>
                <div className="mt-1 text-[10px] text-base-content/50">
                  {formatNumberId(row.value)}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-12 border border-base-300 bg-base-200/30 px-4 py-3 md:col-span-8">
                <div className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-base-content/50">
                  Audit Insight
                </div>
                <div className="font-semibold leading-snug">
                  {auditExplanation(row.status)}
                </div>
              </div>
              <div className="col-span-12 border border-base-300 px-4 py-3 md:col-span-4">
                <div className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-base-content/50">
                  Operational Record
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="text-base-content/50">Created By</div>
                  <div className="text-right font-bold">{row.createdBy}</div>
                  <div className="text-base-content/50">Note</div>
                  <div className="text-right font-bold">
                    {row.note?.trim() ? row.note : "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="flex justify-end">
        <Button type="button" color="primary" size="sm" className="font-bold" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function AuditBox({
  label,
  value,
  title
}: {
  label: string
  value: string
  title?: string
}) {
  return (
    <div className="flex min-h-[70px] flex-col justify-center overflow-hidden border border-base-300 px-1.5 py-2 text-center">
      <div className="text-[10px] font-extrabold uppercase tracking-wide text-base-content/50">
        {label}
      </div>
      <div
        className="mt-1 text-sm font-black leading-tight tabular-nums sm:text-base"
        title={title}>
        {value}
      </div>
    </div>
  )
}
